import {
  computeSolanaTokenHash,
  decodeSolanaCommitmentRecord,
  decodeSolanaRecoveryRecord,
  decodeSolanaSwapReceipt,
  getSolanaAccounts,
  readSolanaRecoveryRecords,
  solanaPoolAddresses,
  solanaPubkeyBytes,
  solanaRecoveryPayloadDigest,
  SOLANA_TOKEN_PROGRAM_ID,
  SOLANA_TOKEN_2022_PROGRAM_ID,
} from "@stealthos/sdk";
import { ethers } from "ethers";
import type {
  SpendingKeys,
  JoinSplitNote,
  StoredJoinSplitNote,
} from "../../joinsplit/types";
import {
  formatInboxPublicKeyHex,
  getInboxSecretKeyCandidates,
} from "../../utils/inbox-crypto";
import type { SolanaNetworkConfig } from "../../config/solana";
import {
  encryptOnchainNoteRecovery,
  decryptOnchainNoteRecovery,
  type OnchainRecoveryContext,
} from "../onchainRecoveryCodec";
import { createSolanaFlatNote, solanaMintFromNoteTokenAddress } from "./notes";
import type { SolanaRpc } from "./rpc";
import { assertNotAborted, fail } from "./common";
import { synchronizeSolanaNotes } from "./sync";
import type { SolanaLeafCache } from "./leafCache";

export interface SolanaRecoveryPublication {
  kind: 1 | 2 | 3;
  locator: `0x${string}`;
  payload: Uint8Array;
}

export function solanaRecoveryContext(
  network: Pick<SolanaNetworkConfig, "chainDomainId" | "programId">,
  kind: 1 | 2 | 3,
  locator: string,
): OnchainRecoveryContext {
  return {
    chain: "solana",
    chainDomain: ethers.toBeHex(network.chainDomainId, 32),
    pool: ethers.hexlify(solanaPubkeyBytes(network.programId, "programId")),
    kind,
    locator,
  };
}

export async function prepareSolanaNoteRecovery(
  network: SolanaNetworkConfig,
  note: JoinSplitNote,
  inboxPublicKey: string,
): Promise<SolanaRecoveryPublication> {
  if (!note.tokenData || ![3, 4].includes(note.tokenData.tokenType))
    fail("Solana note has no recovery token identity");
  const locator = note.commitment.toLowerCase() as `0x${string}`;
  const payload = await encryptOnchainNoteRecovery(
    {
      kind: 1,
      random: note.random,
      value: note.value,
      tokenAddress: note.tokenData.tokenAddress,
      tokenType: note.tokenData.tokenType as 3 | 4,
    },
    inboxPublicKey,
    solanaRecoveryContext(network, 1, locator),
  );
  return { kind: 1, locator, payload };
}

export async function prepareSolanaSwapRecovery(
  network: SolanaNetworkConfig,
  spendingKeys: SpendingKeys,
  firstNullifier: string,
  seed: { random: bigint; outputMint: string | null },
  tokenType: 3 | 4 = 3,
): Promise<SolanaRecoveryPublication> {
  const locator = firstNullifier.toLowerCase() as `0x${string}`;
  const payload = await encryptOnchainNoteRecovery(
    {
      kind: 2,
      random: seed.random,
      tokenAddress: seed.outputMint
        ? ethers.hexlify(solanaPubkeyBytes(seed.outputMint, "mint"))
        : ethers.toBeHex(0n, 32),
      tokenType,
    },
    formatInboxPublicKeyHex(spendingKeys.spendingKey),
    solanaRecoveryContext(network, 2, locator),
  );
  return { kind: 2, locator, payload };
}

/** Verify a standalone recovery publication at finality. Protected spends bind records atomically. */
export async function assertSolanaRecoveryPublished(
  rpc: SolanaRpc,
  network: SolanaNetworkConfig,
  publications: readonly SolanaRecoveryPublication[],
): Promise<void> {
  if (!publications.length) return;
  const addresses = solanaPoolAddresses(network.programId);
  const { accounts } = await getSolanaAccounts(
    rpc,
    publications.map((p) =>
      addresses.recoveryRecord(
        p.kind,
        p.locator,
        solanaRecoveryPayloadDigest(p.payload),
      ),
    ),
    "finalized",
  );
  publications.forEach((expected, index) => {
    const info = accounts[index];
    if (!info || info.owner !== addresses.programId || info.executable)
      fail(
        "Encrypted recovery data is not finalized on Solana; transaction was not submitted",
      );
    const actual = decodeSolanaRecoveryRecord(info.data);
    if (
      actual.kind !== expected.kind ||
      actual.locator !== expected.locator ||
      ethers.hexlify(actual.payload) !== ethers.hexlify(expected.payload)
    )
      fail(
        "On-chain recovery data differs from the prepared note; transaction was not submitted",
      );
  });
}

/** Cold recovery reads only public finalized program state. No inbox or journal is required. */
export async function recoverSolanaNotesFromChain(params: {
  rpc: SolanaRpc;
  network: SolanaNetworkConfig;
  spendingKeys: SpendingKeys;
  cache?: SolanaLeafCache;
  signal?: AbortSignal;
}) {
  const { rpc, network, spendingKeys } = params;
  const addresses = solanaPoolAddresses(network.programId);
  const scan = await readSolanaRecoveryRecords(rpc, addresses.programId);
  const secretKeys = getInboxSecretKeyCandidates(spendingKeys.spendingKey);
  const notes: StoredJoinSplitNote[] = [];
  for (const { record } of scan.records) {
    assertNotAborted(params.signal, "during on-chain recovery");
    if (record.kind === 3) continue;
    let plaintext;
    try {
      plaintext = await decryptOnchainNoteRecovery(
        record.payload,
        secretKeys,
        solanaRecoveryContext(network, record.kind, record.locator),
      );
    } catch {
      continue;
    } // Other wallets' ciphertext and malformed candidates are not ours.
    const mint = solanaMintFromNoteTokenAddress(plaintext.tokenAddress);
    let commitment = record.locator;
    let value: bigint;
    if (plaintext.kind === 2) {
      const { accounts } = await getSolanaAccounts(
        rpc,
        [addresses.swapReceipt(record.locator)],
        "finalized",
      );
      const info = accounts[0];
      if (!info) continue; // Prepublished swap that never settled.
      if (info.owner !== addresses.programId || info.executable)
        fail("Invalid on-chain swap recovery receipt");
      const receipt = decodeSolanaSwapReceipt(info.data);
      if (receipt.outputMint !== (mint ?? "11111111111111111111111111111111"))
        continue;
      commitment = receipt.outputCommitment;
      value = receipt.outputAmount;
    } else {
      value = plaintext.value;
    }
    const { accounts } = await getSolanaAccounts(
      rpc,
      [addresses.commitmentRecord(commitment)],
      "finalized",
    );
    const info = accounts[0];
    if (!info) continue; // Prepublication alone is not a deposit.
    if (info.owner !== addresses.programId || info.executable)
      fail("Invalid on-chain commitment record");
    const location = decodeSolanaCommitmentRecord(info.data);
    const note = await createSolanaFlatNote({
      recipientMpk: spendingKeys.mpk,
      network,
      treeIndex: location.treeIndex,
      mint,
      tokenProgram:
        plaintext.tokenType === 4
          ? SOLANA_TOKEN_2022_PROGRAM_ID
          : SOLANA_TOKEN_PROGRAM_ID,
      random: plaintext.random,
      value,
      tokenHash: computeSolanaTokenHash({
        cluster: network.cluster,
        programId: network.programId,
        poolConfig: addresses.poolConfig,
        treeIndex: location.treeIndex,
        mint,
      }),
    });
    if (note.commitment.toLowerCase() !== commitment) continue; // Decryptable but invalid candidates cannot block valid later ones.
    if (location.commitment !== commitment)
      fail(
        "Legacy Solana leaf requires public hydration before independent recovery",
      );
    note.leafIndex = location.leafIndex;
    notes.push({ note, spent: false, timestamp: 0 });
  }
  const result = await synchronizeSolanaNotes({
    ...params,
    notes,
    requireDurableLeaves: true,
    maxTreeScans: Number.MAX_SAFE_INTEGER,
  });
  return {
    ...result,
    scannedRecords: scan.records.length,
    recoveredCandidates: notes.length,
  };
}
